import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { MessagePopupService } from '../../shared/components/message-popup/message-popup.service';
import { AuthService } from '../services/auth.service';
import { ServiceStatusService } from '../services/service-status.service';

//Marker set on an error the API refused with 429. The popup for it is shown here, once, so a
//caller only needs to know that the user has already been told - see isRateLimited() - and must
//not add a message of its own. Without that check a 429 on a seat reservation would answer with
//"locul a fost rezervat deja de altcineva", which is not what happened.
const RATE_LIMITED = '_softransRateLimited';

//Marker set on an error caused by the API being unavailable (maintenance 503 or no connection at
//all). The outage dialog is shown once by ServiceStatusService, so a caller only needs to know
//that this failure has already been explained - see isServiceUnavailable() - and must not add a
//message of its own, which would blame the action the user attempted instead of the outage.
const SERVICE_UNAVAILABLE = '_softransServiceUnavailable';

type ServiceUnavailableMarker = { [SERVICE_UNAVAILABLE]?: boolean };

//Used when the API answers 429 with an empty or non-textual body. The API normally sends its own
//Romanian message including the delay; Retry-After cannot be read here because the API exposes
//only Content-Range through Access-Control-Expose-Headers.
const RATE_LIMIT_MESSAGE = 'Ați făcut prea multe cereri într-un interval scurt. '
  + 'Vă rugăm să încercați din nou mai târziu.';

//Only one rate-limit popup at a time: several requests in flight can come back 429 together and
//each dialog would otherwise stack on top of the previous one. Held here rather than in a service
//so that whenRateLimitPopupClosed() can be awaited by a caller that is about to reload the page -
//a reload while this dialog is open would take the explanation away with it.
let rateLimitPopup: Promise<unknown> | null = null;

export function isRateLimited(error: unknown): boolean {
  return !!error && (error as any)[RATE_LIMITED] === true;
}

/**
 * True when the failure was caused by the service being unavailable, either because this
 * interceptor already reported it (the marker) or because the error itself is a 503/status 0.
 */
export function isServiceUnavailable(error: unknown): boolean {
  if (error === null || error === undefined) {
    return false;
  }

  if ((error as ServiceUnavailableMarker)[SERVICE_UNAVAILABLE] === true) {
    return true;
  }

  return error instanceof HttpErrorResponse && (error.status === 0 || error.status === 503);
}

/**
 * The reason the API gave for refusing a request (a 400 with a Romanian text body, such as an
 * unverified discount), or the caller's own message when there is none to show.
 */
export function refusalMessage(error: unknown, fallback: string): string {
  const isRefusalWithReason = error instanceof HttpErrorResponse && error.status === 400
    && typeof error.error === 'string' && error.error.trim() !== '';
  return isRefusalWithReason ? error.error : fallback;
}

/** Resolves once the user has closed the rate-limit popup, or immediately when none is open. */
export function whenRateLimitPopupClosed(): Promise<unknown> {
  return rateLimitPopup ?? Promise.resolve();
}

export const errorCatchingInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const messagePopupService = inject(MessagePopupService);
  const serviceStatus = inject(ServiceStatusService);

  return next(req).pipe(
    catchError(err => {
      console.error("errorCatchingInterceptor", err);
      if (err.status === 428) {
        messagePopupService.showMessage("Sesiunea a expirat, veti fi redirectionat catre pagina de login.", 'error');
        auth.logout();
      }
      if (err.status === 403 && auth.isLoggedIn()) {
        messagePopupService.showMessage(err.error, 'error');
        auth.logout();
      }
      if (err.status === 429) {
        err[RATE_LIMITED] = true;
        if (rateLimitPopup === null) {
          //the API sends its own Romanian message, including how long the caller has to wait
          const message = typeof err.error === 'string' && err.error.trim() !== ''
            ? err.error
            : RATE_LIMIT_MESSAGE;
          rateLimitPopup = messagePopupService.showMessage(message, 'warning')
            .finally(() => rateLimitPopup = null);
        }
      }
      if (err.status === 503 || err.status === 0) {
        err[SERVICE_UNAVAILABLE] = true;
        serviceStatus.reportFailure(err);
      }
      return throwError(() => err);
    })
  );
};
